import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { microSessionService } from '@/services/microSessionService';
import { progressTrackingService } from '@/services/progressTrackingService';
import { MicroSession } from '@/data/microSessions';
import { ProgressStatus, UserProgressDetailed } from '@/types/progress';

interface MicroPlaylistState {
  dailyFocus: MicroSession | null;
  playlist: MicroSession[];
  continueLearning: MicroSession[];
  explore: MicroSession[];
  stats: {
    completed: number;
    inProgress: number;
  };
  loading: boolean;
  progressMap: Record<string, ProgressStatus>;
  refresh: () => Promise<void>;
}

export const useMicroPlaylist = (): MicroPlaylistState => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<UserProgressDetailed[]>([]);

  const loadProgress = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Get all progress - filter to micro session IDs from the catalog
      const allProgress = await progressTrackingService.getAllProgress();
      // Get valid micro session IDs from catalog for filtering
      const microSessionIds = new Set(microSessionService.getCatalog().map(s => s.id));
      const microProgress = allProgress
        .filter(p => microSessionIds.has(p.lessonId))
        .map(p => ({
          id: p.id,
          userId: p.userId,
          contentType: 'micro',
          contentId: p.lessonId,
          progressData: p.progressData,
          percentageComplete: p.progressPercent,
          timeSpentSeconds: p.timeSpentSeconds,
          status: p.status,
          milestonesCompleted: [],
          currentMilestone: undefined,
          firstAccessedAt: p.startedAt,
          lastAccessedAt: p.startedAt,
          completedAt: p.completedAt,
          createdAt: p.startedAt,
          updatedAt: p.startedAt
        } as UserProgressDetailed));
      setProgress(microProgress);
    } catch (error) {
      console.warn('[useMicroPlaylist] Failed to load micro progress', error);
      setProgress([]); // Ensure we set empty array on error, not null
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadProgress();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        loadProgress();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  const dailyFocus = useMemo(() => {
    if (!user) return null;
    return microSessionService.getDailyFocus(user.id);
  }, [user, progress]);

  const continueLearning = useMemo(() => {
    // Defensive check - ensure progress is an array
    const progressArray = progress || [];

    const simplified = progressArray.map((item) => ({
      contentId: item.contentId,
      status: item.status as ProgressStatus,
      percentageComplete: item.percentageComplete,
      completedAt: item.completedAt
    }));

    const continueSessions = microSessionService.getContinueLearning(simplified).slice(0, 3);

    // Debug logging for continue learning
    console.log('[useMicroPlaylist] Continue Learning calculated:', {
      totalProgress: progressArray.length,
      simplified: simplified.map(s => ({ id: s.contentId, status: s.status, pct: s.percentageComplete })),
      continueSessions: continueSessions.map(s => s.id)
    });

    return continueSessions;
  }, [progress]);

  const playlist = useMemo(() => {
    const exclude = dailyFocus ? [dailyFocus.id] : [];
    return microSessionService
      .getPlaylist(exclude)
      .filter((session) => !continueLearning.some((item) => item.id === session.id))
      .slice(0, 4);
  }, [dailyFocus, continueLearning]);

  const explore = useMemo(() => {
    return microSessionService.getExploreSessions(3);
  }, []);

  const stats = useMemo(() => {
    // Defensive check - ensure progress is an array
    const progressArray = progress || [];
    const completed = progressArray.filter((item) => item.status === 'completed');
    const inProgress = progressArray.filter((item) => item.status === 'in_progress');

    // Debug logging to track progress counting
    console.log('[useMicroPlaylist] Stats calculated:', {
      totalProgress: progressArray.length,
      completed: completed.length,
      completedItems: completed.map(p => ({ id: p.contentId, status: p.status, percentage: p.percentageComplete })),
      inProgress: inProgress.length,
      inProgressItems: inProgress.map(p => ({ id: p.contentId, status: p.status, percentage: p.percentageComplete }))
    });

    return {
      completed: completed.length,
      inProgress: inProgress.length
    };
  }, [progress]);

  return {
    dailyFocus,
    playlist,
    continueLearning,
    explore,
    stats,
    loading,
    progressMap: useMemo(() => {
      // Defensive check - ensure progress is an array
      const progressArray = progress || [];
      return progressArray.reduce((acc, item) => {
        acc[item.contentId] = item.status as ProgressStatus;
        return acc;
      }, {} as Record<string, ProgressStatus>);
    }, [progress]),
    refresh: loadProgress
  };
};
